import React, { useState, useEffect } from 'react';
import LegalModal from './LegalModal';

interface CookieConsentProps {
  onAccept: () => void;
  onDecline: () => void;
}

export const CookieConsent: React.FC<CookieConsentProps> = ({ onAccept, onDecline }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAccepted, setIsAccepted] = useState(false);
  const [showPolicyModal, setShowPolicyModal] = useState(false);

  useEffect(() => {
    // Проверяем, было ли уже дано согласие
    const hasConsent = localStorage.getItem('cookie_consent');
    const consentDate = localStorage.getItem('cookie_consent_date');
    
    // Если согласие ещё не дано, показываем баннер
    if (!hasConsent || hasConsent !== 'true') {
      // Показываем с задержкой для лучшего UX
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    setIsAccepted(true);
    
    // Записываем согласие в localStorage
    localStorage.setItem('cookie_consent', 'true');
    localStorage.setItem('cookie_consent_date', new Date().toISOString());
    
    // Отправляем на сервер
    sendConsentToServer(true);
    
    // Вызываем колбэк для запуска метрик
    onAccept();
    
    // Скрываем баннер
    setIsVisible(false);
  };

  const handleDecline = () => {
    // Записываем отказ в localStorage
    localStorage.setItem('cookie_consent', 'false');
    localStorage.setItem('cookie_consent_date', new Date().toISOString());
    
    // Отправляем на сервер
    sendConsentToServer(false);
    
    // Вызываем колбэк
    onDecline();
    
    // Скрываем баннер
    setIsVisible(false);
  };

  const sendConsentToServer = async (accepted: boolean) => {
    try {
      // Генерируем уникальный код сессии
      const sessionCode = `cookie_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Собираем данные сессии
      const consentData = {
        session_code: sessionCode,
        accepted: accepted,
        consent_date: new Date().toISOString(),
        user_agent: navigator.userAgent,
        cookies_enabled: navigator.cookieEnabled,
        screen_resolution: `${screen.width}x${screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      // Отправляем на сервер
      const response = await fetch('/api/cookie-consent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(consentData),
      });

      if (!response.ok) {
        console.error('Failed to save cookie consent');
      }
    } catch (error) {
      console.error('Error sending cookie consent:', error);
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="cookie-consent-overlay" style={{
      position: 'fixed',
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
      backgroundColor: 'rgba(10, 8, 20, 0.85)',
      backdropFilter: 'blur(10px)',
      padding: '20px',
      zIndex: 10000,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    }}>
      <div className="cookie-consent-banner" style={{
        backgroundColor: 'rgba(13, 25, 48, 0.7)',
        backdropFilter: 'blur(20px)',
        borderRadius: '20px',
        padding: '32px',
        maxWidth: '600px',
        width: '100%',
        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.5)',
        border: '1px solid rgba(212, 175, 55, 0.3)',
      }}>
        <div className="cookie-consent-content">
          <h3 style={{
            fontSize: '20px',
            fontWeight: 'bold',
            marginBottom: '16px',
            color: '#fff',
          }}>
            🍪 Мы используем cookies
          </h3>
          
          <p style={{
            fontSize: '14px',
            lineHeight: '1.6',
            marginBottom: '20px',
            color: '#ccc',
          }}>
            Мы используем файлы cookie и Яндекс.Метрику для анализа работы сайта,
            персонализации контента и улучшения пользовательского опыта.
            Продолжая использовать сайт, вы соглашаетесь с нашей{' '}
            <button
              type="button"
              onClick={() => setShowPolicyModal(true)}
              style={{
                background: 'none',
                border: 'none',
                color: '#7b68ee',
                textDecoration: 'underline',
                cursor: 'pointer',
                padding: 0,
                fontSize: '14px',
                fontFamily: 'inherit',
              }}
            >
              Политикой конфиденциальности
            </button>
            .
          </p>

          <div className="cookie-consent-options" style={{
            backgroundColor: '#16213e',
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '20px',
          }}>
            <label style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              cursor: 'pointer',
              fontSize: '14px',
              color: '#fff',
            }}>
              <input
                type="checkbox"
                checked={isAccepted}
                onChange={(e) => setIsAccepted(e.target.checked)}
                style={{
                  marginTop: '2px',
                  width: '18px',
                  height: '18px',
                  cursor: 'pointer',
                  accentColor: '#7b68ee',
                }}
              />
              <span>
                <strong>Я согласен на использование cookies и Яндекс.Метрики</strong>
                <br />
                <span style={{ fontSize: '12px', color: '#999' }}>
                  Это поможет нам сделать сайт лучше. Вы можете отозвать согласие в любой момент.
                </span>
              </span>
            </label>
          </div>

          <div className="cookie-consent-buttons" style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end',
          }}>
            <button
              onClick={handleDecline}
              style={{
                padding: '12px 24px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: 'transparent',
                color: '#999',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#fff'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#999'}
            >
              Отклонить
            </button>
            
            <button
              onClick={handleAccept}
              disabled={!isAccepted}
              style={{
                padding: '12px 24px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: isAccepted ? '#7b68ee' : '#444',
                color: '#fff',
                cursor: isAccepted ? 'pointer' : 'not-allowed',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.2s',
                opacity: isAccepted ? 1 : 0.6,
              }}
              onMouseEnter={(e) => {
                if (isAccepted) e.currentTarget.style.backgroundColor = '#6a5acd';
              }}
              onMouseLeave={(e) => {
                if (isAccepted) e.currentTarget.style.backgroundColor = '#7b68ee';
              }}
            >
              Принять и продолжить
            </button>
          </div>

          <p style={{
            fontSize: '11px',
            color: '#666',
            marginTop: '16px',
            textAlign: 'center',
          }}>
            В соответствии с Федеральным законом № 152-ФЗ «О персональных данных»
          </p>
        </div>
      </div>

      <LegalModal
        isOpen={showPolicyModal}
        onClose={() => setShowPolicyModal(false)}
        docId="policy"
      />
    </div>
  );
};

export default CookieConsent;
